import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from 'context/CartContext';
import { useAuth } from 'context/AuthContext';
import useClickOutside from 'hooks/useClickOutside';
import useFocusTrap from 'hooks/useFocusTrap';
import useSiteNav from 'hooks/useSiteNav';
import { resolveNavLink, hrefOf, actionOf } from 'services/resolveNavLink';
import logger from 'utils/logger';
import 'styles/components/header.css';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Site CMS: navigation singleton (STORY-13.4.05). Header reads from
  // `/site_settings/nav.header[]` via the 60s cache. Each item is
  // resolved via `resolveNavLink` to a final href (or an action
  // descriptor for `cart-drawer`/etc.). DEFAULT_NAV is rendered
  // synchronously while the fetch is in flight, so first paint is
  // unchanged (zero LCP impact).
  const { nav } = useSiteNav();
  const primaryNav = useMemo(() => {
    const items = Array.isArray(nav?.header) ? nav.header : [];
    return items.map((item, index) => {
      const resolved = resolveNavLink(item);
      return {
        key: `${item.label || 'nav'}-${index}`,
        label: item.label,
        href: hrefOf(resolved),
        action: actionOf(resolved),
        openNewTab: item.open_new_tab === true,
      };
    });
  }, [nav]);
  const { getTotalItems } = useCart();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const cartItemCount = getTotalItems();

  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const hamburgerRef = useRef(null);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = '';
  }, []);

  // Dismiss via keyboard (Esc) returns focus to the hamburger trigger so
  // keyboard users aren't dropped at the top of the document (STORY-08.3.01).
  const dismissMobileMenu = useCallback(() => {
    closeMobileMenu();
    hamburgerRef.current?.focus();
  }, [closeMobileMenu]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => {
      const next = !prev;
      document.body.style.overflow = next ? 'hidden' : '';
      return next;
    });
  };

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
    setShowUserMenu(false);
  }, [location, closeMobileMenu]);

  // Cleanup body overflow on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
      navigate('/');
    } catch (error) {
      logger.error('Logout error:', error);
    }
  };

  // Close user dropdown on outside click
  useClickOutside(userMenuRef, () => setShowUserMenu(false), showUserMenu);

  // Close mobile menu on outside click
  useClickOutside(mobileMenuRef, closeMobileMenu, isMobileMenuOpen);

  // Focus trap for mobile menu — Esc dismisses and restores focus to the trigger
  useFocusTrap(mobileMenuRef, isMobileMenuOpen, dismissMobileMenu);

  const userDisplayName =
    currentUser?.displayName ||
    currentUser?.email?.split('@')[0] ||
    'User';

  return (
    <header className="header" role="banner">
      <div className="header__container">
        {/* Nav left — desktop only. Items rendered from /site_settings/nav.header[]
         * via useSiteNav + resolveNavLink (STORY-13.4.05). DEFAULT_NAV
         * paints synchronously, so first paint is unchanged. Action-
         * type items render as buttons; everything else as <Link>
         * (internal) or <a target="_blank"> (open_new_tab + external). */}
        <nav className="header__nav-left" aria-label="Main navigation">
          {primaryNav.map((n) => {
            if (n.action) {
              return (
                <button
                  key={n.key}
                  type="button"
                  className="header__nav-link header__nav-link--action"
                  onClick={() => logger.log(`Header nav action: ${n.action}`)}
                  data-action={n.action}
                >
                  {n.label}
                </button>
              );
            }
            if (n.openNewTab) {
              return (
                <a
                  key={n.key}
                  href={n.href}
                  className="header__nav-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {n.label}
                </a>
              );
            }
            return (
              <Link
                key={n.key}
                to={n.href}
                className={`header__nav-link ${isActive(n.href) ? 'header__nav-link--active' : ''}`}
              >
                {n.label}
              </Link>
            );
          })}
        </nav>

        {/* Center wordmark */}
        <Link to="/" className="header__wordmark" onClick={closeMobileMenu}>
          <span className="header__wordmark-curated">curated</span>
          <span className="header__wordmark-lagos">LAGOS</span>
        </Link>

        {/* Right — icons */}
        <div className="header__icons">
          {/* Account */}
          {currentUser ? (
            <div className="header__user-menu" ref={userMenuRef}>
              <button
                className="header__icon-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserMenu(!showUserMenu);
                }}
                aria-label="User menu"
                aria-expanded={showUserMenu}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 21c0-4.5 3.5-7 8-7s8 2.5 8 7" />
                </svg>
              </button>
              {showUserMenu && (
                <div className="header__dropdown">
                  <span className="header__dropdown-name">{userDisplayName}</span>
                  <Link
                    to="/account"
                    className="header__dropdown-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      closeMobileMenu();
                    }}
                  >
                    My Account
                  </Link>
                  <button
                    type="button"
                    className="header__dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLogout();
                      closeMobileMenu();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/login"
              className="header__icon-btn"
              aria-label="Account"
              onClick={closeMobileMenu}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4.5 3.5-7 8-7s8 2.5 8 7" />
              </svg>
            </Link>
          )}

          {/* Cart */}
          <Link
            to="/cart"
            className="header__icon-btn header__cart"
            aria-label="Shopping cart"
            onClick={closeMobileMenu}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M5 7h14l-1.5 11.5a2 2 0 0 1-2 1.5h-7a2 2 0 0 1-2-1.5L5 7Z" />
              <path d="M9 7a3 3 0 0 1 6 0" />
            </svg>
            {cartItemCount > 0 && (
              <span className="header__cart-badge" aria-label={`${cartItemCount} items in cart`}>
                {cartItemCount}
              </span>
            )}
          </Link>

          {/* Mobile hamburger */}
          <button
            ref={hamburgerRef}
            className="header__hamburger"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="header-mobile-overlay"
            onClick={toggleMobileMenu}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu overlay — permanently mounted so it can fade in/out via
          opacity + visibility (STORY-08.3.01). aria-hidden + visibility:hidden
          keep it out of the a11y tree and tab order while closed. */}
      <div
        id="header-mobile-overlay"
        className="header__mobile-overlay"
        ref={mobileMenuRef}
        data-open={isMobileMenuOpen}
        aria-hidden={!isMobileMenuOpen}
      >
        <nav className="header__mobile-nav" aria-label="Mobile navigation">
            {/* Same Site-CMS-driven primaryNav as the desktop strip — same
             * useSiteNav + resolveNavLink data source. */}
            {primaryNav.map((n) => {
              if (n.action) {
                return (
                  <button
                    key={n.key}
                    type="button"
                    className="header__mobile-link"
                    onClick={() => { logger.log(`Header nav action: ${n.action}`); dismissMobileMenu(); }}
                    data-action={n.action}
                  >
                    {n.label}
                  </button>
                );
              }
              if (n.openNewTab) {
                return (
                  <a
                    key={n.key}
                    href={n.href}
                    className="header__mobile-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={dismissMobileMenu}
                  >
                    {n.label}
                  </a>
                );
              }
              return (
                <Link
                  key={n.key}
                  to={n.href}
                  className={`header__mobile-link ${isActive(n.href) ? 'header__mobile-link--active' : ''}`}
                  onClick={dismissMobileMenu}
                >
                  {n.label}
                </Link>
              );
            })}
            {currentUser ? (
              <>
                <Link
                  to="/account"
                  className="header__mobile-link"
                  onClick={dismissMobileMenu}
                >
                  Account
                </Link>
                <button
                  type="button"
                  className="header__mobile-link header__mobile-logout"
                  onClick={() => {
                    handleLogout();
                    dismissMobileMenu();
                  }}
                >
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="header__mobile-link"
                onClick={dismissMobileMenu}
              >
                Sign in
              </Link>
            )}
          </nav>
        </div>
    </header>
  );
};

export default Header;
